import { lazy, Suspense } from 'react'
import { createBrowserRouter } from 'react-router-dom'
import { FullScreenSpinner, RequireAuth, RequireRole } from '@/auth/guards'
import { CONSOLE_ROLES } from '@/auth/roles'
import { LoginPage, NoAccessPage } from '@/auth/LoginPage'
import { PhoneRoot } from '@/features/phone/PhoneRoot'
import { RequireDeviceReady, RequireUnlocked } from '@/features/phone/guards'
import { PhoneLayout } from '@/features/phone/components/PhoneLayout'
import { DeviceSetupPage } from '@/features/phone/pages/DeviceSetupPage'
import { PinUnlockPage } from '@/features/phone/pages/PinUnlockPage'
import { PhoneHomePage } from '@/features/phone/pages/PhoneHomePage'
import { SyncPage } from '@/features/phone/pages/SyncPage'
import { MorePage } from '@/features/phone/pages/MorePage'
import { gateRoutes } from '@/features/gate/routes'
import { storeRoutes } from '@/features/store/routes'
import { processRoutes } from '@/features/process/routes'
import { packRoutes } from '@/features/pack/routes'
import { ConsoleLayout } from '@/features/console/ConsoleLayout'
import { consoleRoutes } from '@/features/console/routes'
import { PassportPage } from '@/features/passport/PassportPage'
import { RouteError } from './ErrorBoundary'
import { NotFoundPage } from './NotFoundPage'
import { Landing, MarkPhoneSurface, Root } from './routeElements'

const DesignSystemPage = import.meta.env.DEV ? lazy(() => import('./dev/DesignSystemPage')) : null

export const router = createBrowserRouter([
  {
    element: <Root />,
    errorElement: <RouteError />,
    children: [
      { path: '/', element: <Landing /> },
      { path: '/login', element: <LoginPage /> },

      // ---------- public Passport (no login): /p/{lot}, and the pouch link's own shapes ----------
      { path: '/p/:code', element: <PassportPage /> },
      { path: '/p/10/:lot', element: <PassportPage /> },
      { path: '/p/01/:gtin/10/:lot', element: <PassportPage /> },
      {
        path: '/no-access',
        element: (
          <RequireAuth>
            <NoAccessPage />
          </RequireAuth>
        ),
      },

      // ---------- phone surface (offline-first) ----------
      {
        path: '/m',
        element: <PhoneRoot />,
        children: [
          { path: 'setup', element: <DeviceSetupPage /> },
          {
            element: <MarkPhoneSurface />,
            children: [
              {
                element: <RequireDeviceReady />,
                children: [
                  { path: 'unlock', element: <PinUnlockPage /> },
                  {
                    element: (
                      <RequireUnlocked>
                        <PhoneLayout />
                      </RequireUnlocked>
                    ),
                    children: [
                      { index: true, element: <PhoneHomePage /> },
                      { path: 'sync', element: <SyncPage /> },
                      { path: 'more', element: <MorePage /> },
                      ...gateRoutes,
                      ...storeRoutes,
                      ...processRoutes,
                      ...packRoutes,
                      { path: '*', element: <NotFoundPage /> },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },

      // ---------- office console (online) ----------
      {
        path: '/c',
        element: (
          <RequireAuth>
            <RequireRole roles={CONSOLE_ROLES}>
              <ConsoleLayout />
            </RequireRole>
          </RequireAuth>
        ),
        children: consoleRoutes,
      },

      ...(DesignSystemPage
        ? [
            {
              path: '/dev/ui',
              element: (
                <Suspense fallback={<FullScreenSpinner />}>
                  <DesignSystemPage />
                </Suspense>
              ),
            },
          ]
        : []),

      { path: '*', element: <NotFoundPage /> },
    ],
  },
])
